import type { FactureDraft, FactureDuplicateCandidate } from "compta-shared";
import { DateTime } from "luxon";
import { trpc } from "../../main/components/ProviderContainer.tsx";
import ConfirmModal from "./ConfirmModal.tsx";

const formatDate = (date: Date | null | undefined) =>
  date ? DateTime.fromJSDate(date).toFormat("dd/MM/yyyy") : "—";
const formatAmount = (amount: number) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Math.abs(amount) / 100);

const ResolveDraftAsAccountedModal = ({ draft, candidate, onClose }: {
  draft: FactureDraft & { pdfUrl: string };
  candidate: FactureDuplicateCandidate;
  onClose: () => void;
}) => {
  const utils = trpc.useUtils();
  const invoiceQuery = trpc.facture.getFactureById.useQuery(candidate._id);
  const invoice = invoiceQuery.data;
  const pdfQuery = trpc.facture.refreshImportedDocPdfUrl.useQuery({
    docId: invoice?.docId || "",
  }, { enabled: !!invoice?.docId });
  const mutation = trpc.facture.resolveDraftAsAccounted.useMutation({
    onSuccess: async () => {
      await Promise.all([utils.facture.invalidate(), utils.superPdp.invalidate()]);
      onClose();
    },
  });

  return <ConfirmModal show title="Confirmer : déjà comptabilisée"
    onCancel={() => { if (!mutation.isPending) onClose(); }}
    onConfirm={() => {
      if (invoice && !draft.locked) mutation.mutate({ draftId: draft._id, factureId: invoice._id });
    }}
    confirmLabel="Confirmer : même facture" submittingLabel="Archivage..."
    isSubmitting={mutation.isPending} hideConfirm={!invoice || draft.locked === true}>
    <p>Vérifiez qu'il s'agit de la même facture avant de la retirer des règlements en attente.</p>
    <div className="border rounded p-3 mb-3">
      <strong>Facture à régler : {draft.libellé}</strong>
      <div>Réf. {draft.modeRef} · {formatDate(draft.dateFacture || draft.datePrelevement)} · {formatAmount(draft.montantCent)}</div>
      <a href={draft.pdfUrl} target="_blank" rel="noreferrer">Voir le PDF de la facture à régler</a>
    </div>
    {invoice ? <div className="border rounded p-3 mb-3">
      <strong>Facture comptabilisée : {invoice.libellé}</strong>
      <div>Réf. {invoice.modeRef} · {formatDate(invoice.date || invoice.datePrelevement)} · {formatAmount(invoice.montantCent)}</div>
      {pdfQuery.data ? <a href={pdfQuery.data} target="_blank" rel="noreferrer">Voir le PDF de la facture</a>
        : <div className="text-muted">{invoice.docId ? "Chargement du PDF..." : "Aucun PDF associé à cette facture."}</div>}
    </div> : <p>{invoiceQuery.isLoading ? "Chargement de la facture..." : "Facture comptabilisée introuvable."}</p>}
    {invoiceQuery.error || pdfQuery.error || mutation.error ? <div className="alert alert-danger" role="alert">
      {mutation.error?.message || invoiceQuery.error?.message || pdfQuery.error?.message}
    </div> : null}
    <p className="mb-0">Cette facture sera classée dans « Déjà comptabilisées » et son PDF sera conservé.
      La facture existante et son rapprochement bancaire ne seront pas modifiés.</p>
  </ConfirmModal>;
};

export default ResolveDraftAsAccountedModal;
